import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { NotificationService } from 'ui-components/services/notification-service';

import { MultiEmailInputModal } from '../../../../../components/modals/multi-email-input-modal';
import { Segment } from '../../../../../services/analytics-services/segment';
import { useModal } from '../../../../../hooks/use-modal';
import { useSalesOrderIdContext } from '../../../../context/sales-order-id-context-provider';
import { useEntitiesSalesOrder } from '../../../../../hooks/entities/selectors/sales-orders';
import { useReturnDataContext } from '../../return-data-provider';
import { sendReturnPaymentReminder } from '../../../../../store/returns/returns.api';
import { getCommaSeparatedEmailsFromRequestDetails, isPendingPayment } from '../../../../../store/returns/returns.helpers';

import { messages } from './messages';
import { RETURN_ACTION_SEND_PAYMENT_REMINDER_CLICKED } from '../../../../../constants/analytics-events';

export function SendPaymentReminderAction() {
  const salesOrderId = useSalesOrderIdContext();
  const salesOrder = useEntitiesSalesOrder(salesOrderId) ?? {};
  const { requestDetails } = useReturnDataContext();

  const { isOpen, open, close } = useModal();

  const storeGuid = salesOrder?.store?.storeGuid ?? '';
  const returnId = requestDetails?.id ?? '';
  const isPaymentPending = isPendingPayment(requestDetails);

  const defaultEmail = useMemo(() => getCommaSeparatedEmailsFromRequestDetails(requestDetails), [requestDetails]);

  const sendPaymentReminder = useCallback(
    async (emails: string[]) => {
      try {
        Segment.trackEvent(RETURN_ACTION_SEND_PAYMENT_REMINDER_CLICKED, {
          type: RETURN_ACTION_SEND_PAYMENT_REMINDER_CLICKED,
          enhanced: true,
          requestId: returnId,
          storeGuid,
          emails,
        });
        await sendReturnPaymentReminder(returnId, storeGuid, emails);

        NotificationService.showGenericSuccessNotification(
          <FormattedMessage {...messages.sendPaymentSuccessTitle} />,
          <FormattedMessage {...messages.sendPaymentSuccessContent} values={{ count: emails.length }} />,
        );

        close();
      } catch {
        NotificationService.showGenericFailureNotification();
      }
    },
    [storeGuid, returnId, close],
  );

  if (!isPaymentPending) return null;

  return (
    <>
      <Button onClick={open} theme='primary'>
        <FormattedMessage {...messages.triggerButtonText} />
      </Button>
      {isOpen ? (
        <MultiEmailInputModal
          defaultEmail={defaultEmail}
          modalBodyText={messages.paymentReminderModalDescription}
          modalHeaderText={messages.paymentReminderModalTitle}
          onClose={close}
          onSend={sendPaymentReminder}
        />
      ) : null}
    </>
  );
}
